import React, { useState, useCallback } from 'react';
import { RatingOption, RatingValue } from '../types';

interface Particle {
  id: number;
  x: number;
  y: number;
  tx: number;
  ty: number;
  color: string;
  size: number;
}

interface RippleEffect {
  id: number;
  x: number;
  y: number;
}

interface RatingButtonProps {
  option: RatingOption;
  selected: boolean;
  onSelect: (value: RatingValue) => void;
  index: number;
}

const RatingButton: React.FC<RatingButtonProps> = ({ option, selected, onSelect, index }) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [ripples, setRipples] = useState<RippleEffect[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const spawnParticles = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    const newParticles: Particle[] = Array.from({ length: 12 }, (_, i) => {
      const angle = (i / 12) * Math.PI * 2;
      const dist = 40 + Math.random() * 40;
      return {
        id: Date.now() + i,
        x: cx,
        y: cy,
        tx: Math.cos(angle) * dist,
        ty: Math.sin(angle) * dist,
        color: option.particleColor,
        size: Math.random() * 6 + 3,
      };
    });

    const newRipple: RippleEffect = { id: Date.now(), x: cx, y: cy };

    setParticles(prev => [...prev, ...newParticles]);
    setRipples(prev => [...prev, newRipple]);

    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.find(np => np.id === p.id)));
    }, 1200);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 600);
  }, [option.particleColor]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    spawnParticles(e);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);
    onSelect(option.value);
  }, [spawnParticles, onSelect, option.value]);

  return (
    <button
      className={`rating-btn relative flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-5 lg:p-6 rounded-2xl cursor-pointer border-2 group
        focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
        ${selected
          ? `${option.borderColor} shadow-lg`
          : 'border-white/10 hover:border-white/20'
        }`}
      style={{
        background: selected ? option.bgGradient : 'rgba(255,255,255,0.04)',
        boxShadow: selected ? `0 0 30px ${option.glowColor}, 0 8px 32px rgba(0,0,0,0.3)` : undefined,
        animation: `slideUp 0.5s ease-out ${index * 0.08}s both`,
      }}
      onClick={handleClick}
      aria-label={`Rate as ${option.label}: ${option.description}`}
      aria-pressed={selected}
    >
      {/* Ripple effects */}
      {ripples.map(r => (
        <span
          key={r.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: r.x,
            top: r.y,
            width: 20,
            height: 20,
            marginLeft: -10,
            marginTop: -10,
            background: option.glowColor,
            animation: 'ripple 0.6s ease-out forwards',
          }}
        />
      ))}

      {/* Particles */}
      {particles.map(p => (
        <span
          key={p.id}
          className="particle"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            background: p.color,
            '--tx': `${p.tx}px`,
            '--ty': `${p.ty}px`,
          } as React.CSSProperties}
        />
      ))}

      {/* Emoji */}
      <span
        className="text-4xl sm:text-5xl lg:text-6xl leading-none select-none"
        style={{
          filter: selected ? `drop-shadow(0 0 12px ${option.glowColor})` : undefined,
          transform: isAnimating ? 'scale(1.2)' : 'scale(1)',
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s',
        }}
        role="img"
        aria-hidden="true"
      >
        {option.emoji}
      </span>

      {/* Label */}
      <span
        className={`text-xs sm:text-sm font-semibold tracking-wide uppercase font-display
          ${selected ? option.color : 'text-slate-400 group-hover:text-slate-300'}`}
        style={{ transition: 'color 0.2s' }}
      >
        {option.label}
      </span>

      {/* Selected checkmark */}
      {selected && (
        <span
          className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{
            background: `linear-gradient(135deg, ${option.glowColor}, ${option.glowColor})`,
            animation: 'bounceIn 0.4s cubic-bezier(0.68,-0.55,0.265,1.55)',
          }}
        >
          ✓
        </span>
      )}
    </button>
  );
};

export default RatingButton;
